import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getStorageValues } from '../CommonFunctions';
import * as Icons from 'react-feather';
import VehiclePhotoModal from './VehiclePhotoModal';

function VehicleDropdown({ onChangeDropdown, required, showLabel = true, isDisabled, selectedValue }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [options, setOptions] = useState([]);
  const [galleryVehicle, setGalleryVehicle] = useState(null);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        if (!baseUrl) {
          console.warn('VehicleDropdown: API base URL missing, skipping fetch.');
          return;
        }
        const userId = getStorageValues('userId');
        const body = { UserId: userId };

        const [vehiclesRes, photosRes] = await Promise.all([
          axios.post(`${baseUrl}/vehicle/getall`, body),
          axios.get(`${baseUrl}/gallery/entities`, {
            params: { entityType: 'vehicle', UserId: userId, filter: 'photos' }
          }).catch(() => null)
        ]);

        if (vehiclesRes?.status === 200 && vehiclesRes.data?.data) {
          const vehicleArray = vehiclesRes.data.data;
          const list = Array.isArray(vehicleArray) && vehicleArray.length > 0 && Array.isArray(vehicleArray[0])
            ? vehicleArray[0]
            : vehicleArray;
          if (!Array.isArray(list)) {
            console.warn('VehicleDropdown: Unexpected vehicle payload format', vehiclesRes.data?.data);
            return;
          }

          const idsWithPhotos = new Set(
            (photosRes?.data?.data || []).map((e) => String(e?.entityId ?? '').trim()).filter(Boolean)
          );

          const mapped = list.map((v) => {
            const id = v.VehicleId?.toString();
            const label = (v.VehicleLabel || `${v.Manufacturer || ''} ${v.ModelName || ''} ${v.Variant || ''} ${v.Segment || ''} ${v.Color || ''} ${v.EngineCC || ''}`)
              .replace(/\s+/g, ' ')
              .trim();
            return ({
              value: id,
              label,
              raw: v,
              hasPhotos: id ? idsWithPhotos.has(id) : false,
            });
          });

          setOptions(mapped);
          if (selectedValue) {
            const match = mapped.find((m) => m.value === selectedValue?.toString());
            setSelectedOption(match || null);
          }
        }
      } catch (err) {
        console.error('Failed to load vehicles', err);
      }
    };
    fetchVehicles();
  }, [selectedValue]);

  useEffect(() => {
    if (!selectedValue) {
      setSelectedOption(null);
      return;
    }
    const match = options.find((m) => m.value === selectedValue?.toString());
    setSelectedOption(match || null);
  }, [selectedValue, options]);

  const handleChange = (option, actionMeta) => {
    setSelectedOption(option);
    if (onChangeDropdown) {
      onChangeDropdown(option, actionMeta);
    }
  };

  return (
    <div>
      {showLabel && (
        <label className="form-label">
          Vehicle {required ? <span style={{ color: 'red' }}>*</span> : null}
        </label>
      )}
      <Select
        name="VehicleId"
        value={selectedOption}
        isClearable
        isDisabled={isDisabled}
        options={options}
        onChange={handleChange}
        placeholder="Select vehicle"
        menuPlacement="auto"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        formatOptionLabel={(option, { context }) => (
          <div className="sk-vehicle-option">
            <span className="sk-vehicle-option-label" title={option?.label || ''}>
              {option?.label || ''}
            </span>
            {context === 'menu' && option?.hasPhotos ? (
              <Icons.Image
                size={16}
                className="sk-vehicle-option-icon"
                title="View photos"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setGalleryVehicle(option);
                }}
              />
            ) : null}
          </div>
        )}
      />

      {galleryVehicle ? (
        <VehiclePhotoModal
          vehicleId={galleryVehicle?.value}
          vehicleLabel={galleryVehicle?.label}
          onClose={() => setGalleryVehicle(null)}
        />
      ) : null}
    </div>
  );
}

export default VehicleDropdown;
